import { motion, AnimatePresence } from "framer-motion";
import { useLocation } from "react-router-dom";
import { useState, useEffect, useRef, ReactNode, useMemo } from "react";
import { useSoundContext } from "@/contexts/SoundContext";

interface PageTransitionProps {
  children: ReactNode;
}

export const PageTransition = ({ children }: PageTransitionProps) => {
  const location = useLocation();
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [showContent, setShowContent] = useState(true);
  const isFirstLoad = useRef(true);
  const previousPath = useRef(location.pathname);
  const { playTransitionSound } = useSoundContext();

  // Generate random particles for dramatic effect
  const particles = useMemo(() => 
    Array.from({ length: 30 }, (_, i) => ({
      id: i,
      x: Math.random() * 100,
      y: Math.random() * 100,
      size: Math.random() * 6 + 2,
      delay: Math.random() * 0.5,
      duration: Math.random() * 1.5 + 1,
    })), []
  );

  useEffect(() => {
    // Skip transition on first load (let CinematicIntro handle it)
    if (isFirstLoad.current) {
      isFirstLoad.current = false;
      previousPath.current = location.pathname;
      return;
    }

    // Only trigger transition if path actually changed
    if (previousPath.current === location.pathname) {
      return;
    }
    previousPath.current = location.pathname;

    // Start transition on route change
    setIsTransitioning(true);
    setShowContent(false);
    
    // Play transition sound
    playTransitionSound("enter");

    // After curtain closes, show content
    const showTimer = setTimeout(() => {
      setShowContent(true);
    }, 1000);

    // After content is ready, open curtain
    const openTimer = setTimeout(() => {
      setIsTransitioning(false);
    }, 1400);

    return () => {
      clearTimeout(showTimer);
      clearTimeout(openTimer);
    };
  }, [location.pathname, playTransitionSound]);

  return (
    <>
      {/* Page Content */}
      <AnimatePresence mode="wait">
        {showContent && (
          <motion.div
            key={location.pathname}
            initial={{ opacity: 0, scale: 0.98 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 1.02 }}
            transition={{ duration: 0.5, ease: "easeOut" }}
          >
            {children}
          </motion.div>
        )}
      </AnimatePresence>

      {/* Cinematic Transition Overlay */}
      <AnimatePresence>
        {isTransitioning && (
          <>
            {/* Background Overlay */}
            <motion.div
              className="fixed inset-0 bg-background z-[99]"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.3 }}
            />

            {/* Top Curtain */}
            <motion.div
              className="fixed inset-x-0 top-0 h-1/2 bg-gradient-to-b from-primary via-primary to-primary/80 z-[100]"
              initial={{ y: "-100%" }}
              animate={{ y: 0 }}
              exit={{ y: "-100%" }}
              transition={{
                duration: 0.7,
                ease: [0.65, 0, 0.35, 1],
              }}
            >
              {/* Top curtain glow */}
              <motion.div
                className="absolute bottom-0 left-0 right-0 h-32 bg-gradient-to-t from-primary-foreground/10 to-transparent"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.3 }}
              />
            </motion.div>

            {/* Bottom Curtain */}
            <motion.div
              className="fixed inset-x-0 bottom-0 h-1/2 bg-gradient-to-t from-primary via-primary to-primary/80 z-[100]"
              initial={{ y: "100%" }}
              animate={{ y: 0 }}
              exit={{ y: "100%" }}
              transition={{
                duration: 0.7,
                ease: [0.65, 0, 0.35, 1],
              }}
            >
              {/* Bottom curtain glow */}
              <motion.div
                className="absolute top-0 left-0 right-0 h-32 bg-gradient-to-b from-primary-foreground/10 to-transparent"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.3 }}
              />
            </motion.div>

            {/* Floating Particles */}
            {particles.map((particle) => (
              <motion.div
                key={particle.id}
                className="fixed rounded-full bg-primary-foreground/60 z-[102] pointer-events-none"
                style={{
                  left: `${particle.x}%`,
                  top: `${particle.y}%`,
                  width: particle.size,
                  height: particle.size,
                }}
                initial={{ 
                  opacity: 0, 
                  scale: 0,
                  y: 50
                }}
                animate={{ 
                  opacity: [0, 1, 1, 0], 
                  scale: [0, 1.5, 1, 0],
                  y: [50, -20, -50, -100]
                }}
                exit={{ opacity: 0, scale: 0 }}
                transition={{
                  duration: particle.duration,
                  delay: particle.delay,
                  ease: "easeOut",
                }}
              />
            ))}

            {/* Ember/Spark Particles */}
            {Array.from({ length: 15 }).map((_, i) => (
              <motion.div
                key={`ember-${i}`}
                className="fixed w-1 h-1 bg-accent rounded-full z-[102] pointer-events-none"
                style={{
                  left: `${20 + Math.random() * 60}%`,
                  top: "50%",
                }}
                initial={{ opacity: 0, scale: 0 }}
                animate={{ 
                  opacity: [0, 1, 0],
                  scale: [0, 2, 0],
                  x: (Math.random() - 0.5) * 200,
                  y: (Math.random() - 0.5) * 200,
                }}
                transition={{
                  duration: 1.2,
                  delay: 0.4 + i * 0.05,
                  ease: "easeOut",
                }}
              />
            ))}

            {/* Center Logo/Text */}
            <motion.div
              className="fixed inset-0 flex items-center justify-center z-[103] pointer-events-none"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.3, delay: 0.2 }}
            >
              <div className="text-center relative">
                {/* Glow behind logo */}
                <motion.div
                  className="absolute inset-0 blur-3xl bg-primary-foreground/20 rounded-full"
                  initial={{ scale: 0, opacity: 0 }}
                  animate={{ scale: 2, opacity: 1 }}
                  exit={{ scale: 3, opacity: 0 }}
                  transition={{ duration: 0.8 }}
                />
                
                <motion.div
                  className="text-5xl md:text-7xl font-bold text-primary-foreground tracking-wider relative"
                  initial={{ y: 30, opacity: 0, letterSpacing: "0.5em" }}
                  animate={{ y: 0, opacity: 1, letterSpacing: "0.2em" }}
                  exit={{ y: -30, opacity: 0, scale: 1.1 }}
                  transition={{ duration: 0.6, delay: 0.3, ease: "easeOut" }}
                >
                  HOLMES
                </motion.div>
                
                <motion.div
                  className="text-xl md:text-2xl text-primary-foreground/80 tracking-[0.4em] mt-3 relative"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -20 }}
                  transition={{ delay: 0.5, duration: 0.5 }}
                >
                  HOLDING CO
                </motion.div>

                {/* Subtitle line */}
                <motion.div
                  className="mt-6 text-sm text-primary-foreground/50 tracking-widest"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  transition={{ delay: 0.7 }}
                >
                  RESTORATION EXPERTS
                </motion.div>
              </div>
            </motion.div>

            {/* Horizontal Decorative Lines */}
            <motion.div
              className="fixed left-0 right-0 top-1/2 -translate-y-1/2 h-[2px] bg-gradient-to-r from-transparent via-primary-foreground/50 to-transparent z-[101]"
              initial={{ scaleX: 0, opacity: 0 }}
              animate={{ scaleX: 1, opacity: 1 }}
              exit={{ scaleX: 0, opacity: 0 }}
              transition={{ duration: 0.8, ease: "easeInOut" }}
            />

            {/* Secondary lines */}
            <motion.div
              className="fixed left-0 right-0 top-[45%] h-px bg-gradient-to-r from-transparent via-primary-foreground/20 to-transparent z-[101]"
              initial={{ scaleX: 0 }}
              animate={{ scaleX: 1 }}
              exit={{ scaleX: 0 }}
              transition={{ duration: 0.6, delay: 0.1, ease: "easeInOut" }}
            />
            <motion.div
              className="fixed left-0 right-0 top-[55%] h-px bg-gradient-to-r from-transparent via-primary-foreground/20 to-transparent z-[101]"
              initial={{ scaleX: 0 }}
              animate={{ scaleX: 1 }}
              exit={{ scaleX: 0 }}
              transition={{ duration: 0.6, delay: 0.1, ease: "easeInOut" }}
            />

            {/* Corner accents */}
            <motion.div
              className="fixed top-8 left-8 w-16 h-16 border-l-2 border-t-2 border-primary-foreground/30 z-[101]"
              initial={{ opacity: 0, scale: 0.5 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.5 }}
              transition={{ duration: 0.4, delay: 0.4 }}
            />
            <motion.div
              className="fixed top-8 right-8 w-16 h-16 border-r-2 border-t-2 border-primary-foreground/30 z-[101]"
              initial={{ opacity: 0, scale: 0.5 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.5 }}
              transition={{ duration: 0.4, delay: 0.4 }}
            />
            <motion.div
              className="fixed bottom-8 left-8 w-16 h-16 border-l-2 border-b-2 border-primary-foreground/30 z-[101]"
              initial={{ opacity: 0, scale: 0.5 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.5 }}
              transition={{ duration: 0.4, delay: 0.4 }}
            />
            <motion.div
              className="fixed bottom-8 right-8 w-16 h-16 border-r-2 border-b-2 border-primary-foreground/30 z-[101]"
              initial={{ opacity: 0, scale: 0.5 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.5 }}
              transition={{ duration: 0.4, delay: 0.4 }}
            />
          </>
        )}
      </AnimatePresence>
    </>
  );
};
